import { DSModal, MODAL_SUB_TYPE_V2, MODAL_TYPE_V2 } from '@elliemae/ds-modal';
import { getAppConfigValue } from '@elliemae/pui-app-sdk';
import {
  useAdjustments,
  useLoanDataFieldSelector,
  useLookupOptions,
} from '@frontend/pricing-search';
import { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setLoanDataValueMultiple } from '../../../components/Form/utils/common';
import { useIsZeroBasedPricing } from '../../../../common/utils/customHooks/useIsZeroBasedPricing';
import Common from '../../../../common/services/Common';
import Session, {
  ALL_SUMMARY_PARAMS,
  IS_BUYSIDE,
  IS_VALIDATE,
  ORIGIN_LOCKID,
  ORIGIN_TRANSACTION_TYPE,
} from '../../../../common/services/Session';
import { isEmpty } from '../../../../common/utils/shared';
import {
  cancelLockAction,
  denyActiveLockAction,
  denyLockRequestAction,
  updateAndConfirmAction,
} from '../../../../data/lockConfirm/actionCreators';
import {
  useCompareDataSelector,
  useOriginalLoanInformationSelector,
} from '../../../../data/useSelectors';
import { PATH_LOCK_SUMMARY } from '../../../../route/constants';
import { FooterButtons } from '../../../components/FooterButtons';
import { CommentModal } from '../CommentModal';
import { getFooterButtons, syncActiveLockId } from './helpers';
import { initiOriginData } from '../../../../common/app-helpers';

export const FooterActionButtons = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [denyLockModal, setDenyLockModal] = useState(false);
  const [cancelLockModal, setCancelLockModal] = useState(false);
  const [denyActiveLockModal, setDenyActiveLockModal] = useState(false);
  const [openLockAndConfirmModal, setOpenLockAndConfirmModal] = useState(false);
  const govtUpfrontFees = useLookupOptions('fee') || [];
  const customFields = useLookupOptions('customFields');

  const lockRequests = useOriginalLoanInformationSelector(
    'lockRequests',
    [],
  ) as any[];
  const { changeCounter: pricingChangesCount = 0 } =
    useCompareDataSelector() as any;
  const loanFieldChanges = useLoanDataFieldSelector(
    'additionalLockInfo.lockCompareResults',
    {},
  ) as any;
  const loanFieldChangesCount = useMemo(
    () => Object.values(loanFieldChanges).flat().length,
    [loanFieldChanges],
  );

  const isValidateSubmitButtonEnabled = useSelector(
    ({
      epps: { lockConfirm: { validateSubmitButtonEnabled = false } = {} } = {},
    }) => validateSubmitButtonEnabled,
    shallowEqual,
  );
  const isZeroBasedPricing = useIsZeroBasedPricing();
  const { setHasPendingAdjustments } = useAdjustments();

  // Clear pending adjustments once lock is confirmed
  useEffect(() => {
    if (openLockAndConfirmModal) {
      setHasPendingAdjustments(false);
    }
  }, [openLockAndConfirmModal, setHasPendingAdjustments]);

  const isUpdateLockButtonDisabled = useMemo(() => {
    const notNewProduct = isEmpty(Session.get(ALL_SUMMARY_PARAMS));
    return (
      Session.get(IS_VALIDATE) &&
      pricingChangesCount === 0 &&
      loanFieldChangesCount === 0 &&
      !isValidateSubmitButtonEnabled &&
      notNewProduct
    );
  }, [
    pricingChangesCount,
    loanFieldChangesCount,
    isValidateSubmitButtonEnabled,
  ]);

  const request = lockRequests.find(
    (req) => req.id === Session.get(ORIGIN_LOCKID),
  );

  const isActiveLock = lockRequests.some(
    ({ requestedStatus }) => requestedStatus === 'Locked',
  );

  const buttonsProps = {
    deny: {
      show: request?.requestedStatus === 'Requested',
      handler: () => setDenyLockModal(true),
    },
    denyActiveLock: {
      show: request?.requestedStatus !== 'Requested' && isActiveLock,
      handler: () => setDenyActiveLockModal(true),
    },
    cancel: {
      show: request?.requestedStatus !== 'Requested' && isActiveLock,
      handler: () => setCancelLockModal(true),
      disabled: isUpdateLockButtonDisabled,
    },
    lockConfirm: {
      show: true,
      handler: () =>
        dispatch(
          updateAndConfirmAction(
            setOpenLockAndConfirmModal,
            isZeroBasedPricing,
          ) as any,
        ),
    },
  };

  const buttons = getFooterButtons(buttonsProps);
  const isBuySide = Session.get(IS_BUYSIDE);

  return (
    <>
      <CommentModal
        title="Deny Lock Request"
        show={denyLockModal}
        onHide={() => setDenyLockModal(false)}
        onConfirm={(comments) =>
          dispatch(denyLockRequestAction(comments) as any)
        }
      />
      <CommentModal
        title="Deny Lock"
        show={denyActiveLockModal}
        onHide={() => setDenyActiveLockModal(false)}
        onConfirm={(comments) =>
          dispatch(denyActiveLockAction(comments) as any)
        }
      />
      <CommentModal
        title="Cancel Lock"
        show={cancelLockModal}
        onHide={() => setCancelLockModal(false)}
        onConfirm={(comments) => dispatch(cancelLockAction(comments) as any)}
      />
      <DSModal
        centered
        isOpen={openLockAndConfirmModal}
        modalSubType={MODAL_SUB_TYPE_V2.SUCCESS}
        modalTitle="Success"
        modalType={MODAL_TYPE_V2.DECISION}
        confirmLabel={isBuySide ? 'Go to Sell Side' : 'Go to Buy Side'}
        onConfirm={async () => {
          if (isBuySide) {
            dispatch(
              setLoanDataValueMultiple([
                { value: null, pathValue: 'loanInformation.loanId' },
                { value: null, pathValue: 'eppsUserName' },
              ]) as any,
            );
            await Common.refreshGetOrigin();
            Session.set(ALL_SUMMARY_PARAMS, {});
            Session.set(
              ORIGIN_TRANSACTION_TYPE,
              getAppConfigValue<string>('partner-ui.dataSourceSellside'),
            );
            const sellSideOrigin = await initiOriginData(
              'getsellsidepricing',
              govtUpfrontFees,
              customFields,
              isZeroBasedPricing,
              dispatch,
              false,
              false,
            );
            syncActiveLockId(sellSideOrigin);
          } else {
            await Common.refreshGetOrigin();
            Session.set(
              ORIGIN_TRANSACTION_TYPE,
              getAppConfigValue<string>('partner-ui.dataSourceBuyside'),
            );
            const buySideOrigin = await initiOriginData(
              'getbuysidepricing',
              govtUpfrontFees,
              customFields,
              isZeroBasedPricing,
              dispatch,
              false,
              false,
            );
            syncActiveLockId(buySideOrigin);
          }
          navigate(PATH_LOCK_SUMMARY);
          setOpenLockAndConfirmModal(false);
        }}
        onReject={() => Common.closeApplication()}
        rejectLabel="Close ICE PPE"
        showRejectButton
        showClose={false}
        version={2}
      >
        <p>Pricing lock is confirmed.</p>
      </DSModal>
      <FooterButtons buttons={buttons} />
    </>
  );
};
